'use client';

import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import type { PopoverProps } from '@frameio/vapor';
import { ListItemContextMenu, Menu } from '@frameio/vapor';

type UseListItemContextMenuArgs = {
  buttonAccessibilityLabel: string;
  content: ReactNode;
  enabled?: boolean;
};

export function useListItemContextMenu({
  buttonAccessibilityLabel,
  content,
  enabled = true,
}: UseListItemContextMenuArgs) {
  const [anchorPosition, setAnchorPosition] =
    useState<PopoverProps['anchorPosition']>(undefined);

  const onContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (!enabled) return;

      // Close before repositioning so right-clicking a new spot while the
      // menu is already open moves it instead of staying anchored in place.
      if (anchorPosition) {
        flushSync(() => setAnchorPosition(undefined));
      }

      event.preventDefault();
      const { clientX: x, clientY: y } = event;
      setAnchorPosition({
        getBoundingClientRect: () => ({
          width: 0,
          height: 0,
          top: y,
          bottom: y,
          left: x,
          right: x,
          x,
          y,
        }),
      });
    },
    [enabled, anchorPosition]
  );

  if (!enabled) {
    return { contextMenu: undefined, onContextMenu: undefined };
  }

  const contextMenu = (
    <>
      <ListItemContextMenu
        buttonAccessibilityLabel={buttonAccessibilityLabel}
        content={content}
        variant="persist-button"
      />
      <Menu
        anchorPosition={anchorPosition}
        content={content}
        isShowing={Boolean(anchorPosition)}
        onRequestChange={(showing) => {
          if (!showing) setAnchorPosition(undefined);
        }}
        placement="right-start"
      />
    </>
  );

  return { contextMenu, onContextMenu };
}
